import React, { ChangeEvent, PropsWithChildren, useContext, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { WalletType } from '@/api/Wallet/Types/Wallet'
import { Modal } from '@/components/CustomModal'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormMultiSelectField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Heading4 } from '@/components/ui/typography'
import { AuthSessionContext } from '@/utils/AuthContext'
import { hasPermission } from '@/utils/permissionsValidator'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { CountryDto } from 'api/Identity/Types/Country'
import {
  CheckStates,
  PspOptionDto,
  PspOptions,
  PspWdCheckPaymentOptionsDto,
  PspWdCheckWalletTypeOptions,
  WalletPaymentProviderOptionDto,
  WalletPaymentProviderOptionType,
} from 'api/Wallet/Types/FullPaymentProviderDto'
import { FlexBox } from 'components/Box'
import {
  FormBaseColumn,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { Container } from 'routes/Administration/Administration.styles'

import MoreButton from '../../../../../components/MoreButton/MoreButton'

type Type = 'fxCFD' | 'ibFxCFD'

const PaymentControlsPSPCheckStateFormUI: React.FC<
  FormikProps<PaymentControlsPSPCheckStateValues> & OuterProps
> = (props) => {
  const { countries, values, setFieldValue, isSubmitting, handleSubmit, resetForm } = props
  const [isEdit, setEdit] = useState<boolean>(false)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [optionsModal, setOptionsModal] = useState<{
    data?: PspOptions
    type?: Type
    paymentOption?: NameDto
  }>({
    data: undefined,
    type: undefined,
    paymentOption: undefined,
  })
  const [frequencyModal, setFrequencyModal] = useState<{
    data?: PspOptions
    paymentOption?: NameDto
    AllowedCount?: number
    type?: Type
  }>({
    data: undefined,
    paymentOption: undefined,
    AllowedCount: undefined,
    type: undefined,
  })
  const [modalFormValues, setModalFormValues] = useState<{
    EUR?: number
    GBP?: number
    PLN?: number
    USD?: number
    ZAR?: number
    Default?: number
  }>({
    EUR: undefined,
    GBP: undefined,
    PLN: undefined,
    USD: undefined,
    ZAR: undefined,
    Default: undefined,
  })
  const [modalFrequencyFormValues, setModalFrequencyFormValues] = useState<{
    AllowedCount?: number
  }>({
    AllowedCount: undefined,
  })
  const [errorModal, setErrorModal] = useState<{
    visible: boolean
    max?: number
  }>({ visible: false })

  const handleSubmitForm = () => {
    handleSubmit()
  }

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const onChangeStatus = (pspOptionId: number, type: Type) => {
    if (type === 'fxCFD') {
      const pspOption = values.fxCFD?.pspOptions.find((x) => x.paymentOption.id === pspOptionId)
      if (pspOption) {
        setFieldValue(
          'fxCFD.pspOptions',
          values.fxCFD?.pspOptions.map((x) =>
            x.paymentOption.id === pspOptionId
              ? {
                  ...x,
                  selected: !pspOption.selected,
                }
              : x
          )
        )
      }
    } else {
      const pspOption = values.ibFxCFD?.pspOptions.find((x) => x.paymentOption.id === pspOptionId)
      if (pspOption) {
        setFieldValue(
          'ibFxCFD.pspOptions',
          values.ibFxCFD?.pspOptions.map((x) =>
            x.paymentOption.id === pspOptionId ? { ...x, selected: !pspOption.selected } : x
          )
        )
      }
    }
  }

  const openOptionModal = (pspOption: PspOptionDto, type: Type) => {
    setOptionsModal({ paymentOption: pspOption.paymentOption, data: pspOption.options, type })
    setModalFormValues({
      EUR: pspOption?.options?.maxAmount?.EUR || undefined,
      GBP: pspOption?.options?.maxAmount?.GBP || undefined,
      PLN: pspOption?.options?.maxAmount?.PLN || undefined,
      USD: pspOption?.options?.maxAmount?.USD || undefined,
      ZAR: pspOption?.options?.maxAmount?.ZAR || undefined,
      Default: pspOption.options?.maxAmount?.Default,
    })
  }

  const openFrequencyModal = (pspOption: PspOptionDto, type: Type) => {
    setFrequencyModal({
      paymentOption: pspOption.paymentOption,
      data: pspOption.options,
      AllowedCount: pspOption.options?.AllowedCount,
      type,
    })
    setModalFrequencyFormValues({
      AllowedCount: pspOption.options?.AllowedCount,
    })
  }

  const handleSubmitModal = () => {
    if (optionsModal.type === 'fxCFD') {
      setFieldValue(
        'fxCFD.pspOptions',
        values.fxCFD?.pspOptions.map((x) =>
          x.paymentOption.id === optionsModal.paymentOption?.id
            ? {
                ...x,
                options: {
                  ...x.options,
                  maxAmount: {
                    EUR: modalFormValues.EUR,
                    GBP: modalFormValues.GBP,
                    PLN: modalFormValues.PLN,
                    USD: modalFormValues.USD,
                    ZAR: modalFormValues.ZAR,
                    Default: x.options?.maxAmount?.Default,
                  },
                },
              }
            : x
        )
      )
    } else {
      setFieldValue(
        'ibFxCFD.pspOptions',
        values.ibFxCFD?.pspOptions.map((x) =>
          x.paymentOption.id === optionsModal?.paymentOption?.id
            ? {
                ...x,
                options: {
                  ...x.options,
                  maxAmount: {
                    EUR: modalFormValues.EUR,
                    GBP: modalFormValues.GBP,
                    PLN: modalFormValues.PLN,
                    USD: modalFormValues.USD,
                    ZAR: modalFormValues.ZAR,
                    Default: x.options?.maxAmount?.Default,
                  },
                },
              }
            : x
        )
      )
    }
    setOptionsModal({ data: undefined, type: undefined })
    setModalFormValues({
      EUR: undefined,
      GBP: undefined,
      PLN: undefined,
      USD: undefined,
      ZAR: undefined,
    })
  }

  const handleSubmitFrequency = () => {
    const max = frequencyModal?.data?.MaxAllowedCount || 10
    const count = modalFrequencyFormValues.AllowedCount || 1

    if (count > max) {
      setFrequencyModal({
        data: undefined,
        paymentOption: undefined,
        AllowedCount: undefined,
        type: undefined,
      })
      setErrorModal({ visible: true, max })
      return
    }

    if (frequencyModal.type === 'fxCFD') {
      setFieldValue(
        'fxCFD.pspOptions',
        values.fxCFD?.pspOptions.map((x) =>
          x.paymentOption.id === frequencyModal.paymentOption?.id
            ? {
                ...x,
                options: {
                  ...x.options!,
                  AllowedCount: modalFrequencyFormValues.AllowedCount!,
                },
              }
            : x
        )
      )
    } else {
      setFieldValue(
        'ibFxCFD.pspOptions',
        values.ibFxCFD?.pspOptions.map((x) =>
          x.paymentOption.id === frequencyModal.paymentOption?.id
            ? {
                ...x,
                options: {
                  ...x.options!,
                  AllowedCount: modalFrequencyFormValues.AllowedCount!,
                },
              }
            : x
        )
      )
    }

    setFrequencyModal({
      data: undefined,
      paymentOption: undefined,
      AllowedCount: undefined,
      type: undefined,
    })
    setModalFrequencyFormValues({ AllowedCount: undefined })
  }

  return (
    <React.Fragment>
      {errorModal.visible && (
        <Modal size='small' width={400} visible onClose={() => setErrorModal({ visible: false })}>
          <Heading4>Maximum Allowed Withdrawal Count for 24H is {errorModal.max}.</Heading4>
          <Container>
            <FlexBox style={{ margin: '20px auto' }} alignItems='center' justifyContent='center'>
              <Button onClick={() => setErrorModal({ visible: false })}>OK</Button>
            </FlexBox>
          </Container>
        </Modal>
      )}
      {optionsModal.paymentOption && (
        <Modal
          width={600}
          visible
          onClose={() => setOptionsModal({ data: undefined, type: undefined })}
        >
          <FormBaseRow>
            <FormBaseColumn style={{ width: '100%' }}>
              <FormBaseFieldBlock>
                <FormBaseRow>
                  <FormBaseFieldLabel>EUR</FormBaseFieldLabel>
                  <FormBaseFieldGroup>
                    <Field
                      value={modalFormValues?.EUR}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        if (!event.target.value) {
                          return setModalFormValues({
                            ...modalFormValues,
                            EUR: undefined,
                          })
                        }
                        setModalFormValues({
                          ...modalFormValues,
                          EUR: Number(event.target.value),
                        })
                      }}
                      name='EUR'
                      type='number'
                      step='1'
                    />
                  </FormBaseFieldGroup>
                </FormBaseRow>
              </FormBaseFieldBlock>
              <FormBaseFieldBlock>
                <FormBaseRow>
                  <FormBaseFieldLabel>GBP</FormBaseFieldLabel>
                  <FormBaseFieldGroup>
                    <Field
                      value={modalFormValues?.GBP}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        if (!event.target.value) {
                          return setModalFormValues({
                            ...modalFormValues,
                            GBP: undefined,
                          })
                        }
                        setModalFormValues({
                          ...modalFormValues,
                          GBP: Number(event.target.value),
                        })
                      }}
                      name='GBP'
                      type='number'
                      min='0'
                      step='any'
                    />
                  </FormBaseFieldGroup>
                </FormBaseRow>
              </FormBaseFieldBlock>
              <FormBaseFieldBlock>
                <FormBaseRow>
                  <FormBaseFieldLabel>PLN</FormBaseFieldLabel>
                  <FormBaseFieldGroup>
                    <Field
                      value={modalFormValues?.PLN}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        if (!event.target.value) {
                          return setModalFormValues({
                            ...modalFormValues,
                            PLN: undefined,
                          })
                        }
                        setModalFormValues({
                          ...modalFormValues,
                          PLN: Number(event.target.value),
                        })
                      }}
                      type='number'
                      min='0'
                      step='any'
                    />
                  </FormBaseFieldGroup>
                </FormBaseRow>
              </FormBaseFieldBlock>
              <FormBaseFieldBlock>
                <FormBaseRow>
                  <FormBaseFieldLabel>USD</FormBaseFieldLabel>
                  <FormBaseFieldGroup>
                    <Field
                      value={modalFormValues?.USD}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        if (!event.target.value) {
                          return setModalFormValues({
                            ...modalFormValues,
                            USD: undefined,
                          })
                        }
                        setModalFormValues({
                          ...modalFormValues,
                          USD: Number(event.target.value),
                        })
                      }}
                      type='number'
                      min='0'
                      step='any'
                    />
                  </FormBaseFieldGroup>
                </FormBaseRow>
              </FormBaseFieldBlock>
              <FormBaseFieldBlock>
                <FormBaseRow>
                  <FormBaseFieldLabel>ZAR</FormBaseFieldLabel>
                  <FormBaseFieldGroup>
                    <Field
                      value={modalFormValues?.ZAR}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        if (!event.target.value) {
                          return setModalFormValues({
                            ...modalFormValues,
                            ZAR: undefined,
                          })
                        }
                        setModalFormValues({
                          ...modalFormValues,
                          ZAR: Number(event.target.value),
                        })
                      }}
                      type='number'
                      min='0'
                      step='any'
                    />
                  </FormBaseFieldGroup>
                </FormBaseRow>
              </FormBaseFieldBlock>
              <Container>
                <FlexBox
                  style={{ margin: '20px auto' }}
                  alignItems='center'
                  justifyContent='center'
                >
                  <Button onClick={handleSubmitModal} type='button'>
                    Submit
                  </Button>
                </FlexBox>
              </Container>
            </FormBaseColumn>
          </FormBaseRow>
        </Modal>
      )}
      {frequencyModal.paymentOption && (
        <Modal
          width={450}
          size='small'
          visible
          onClose={() =>
            setFrequencyModal({
              data: undefined,
              paymentOption: undefined,
              AllowedCount: undefined,
            })
          }
        >
          <FormBaseRow>
            <FormBaseColumn style={{ width: '100%' }}>
              <FormBaseFieldBlock>
                <FormBaseRow>
                  <FormBaseFieldLabel style={{ width: '70%' }}>
                    Allowed Withdrawal Count for last 24H
                  </FormBaseFieldLabel>
                  <FormBaseFieldGroup style={{ width: '30%' }}>
                    <Field
                      type='number'
                      min={frequencyModal.data!.MinAllowedCount}
                      max={frequencyModal.data!.MaxAllowedCount}
                      step={1}
                      value={modalFrequencyFormValues.AllowedCount}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setModalFrequencyFormValues({ AllowedCount: Number(e.target.value) })
                      }
                      disabled={!isEdit}
                    />
                  </FormBaseFieldGroup>
                </FormBaseRow>
              </FormBaseFieldBlock>
            </FormBaseColumn>
          </FormBaseRow>

          <Container>
            <FlexBox style={{ margin: '20px auto' }} alignItems='end' justifyContent='center'>
              <Button onClick={handleSubmitFrequency} type='button'>
                Submit
              </Button>
            </FlexBox>
          </Container>
        </Modal>
      )}
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {!isEdit ? (
                <Button
                  disabled={
                    !hasPermission(permissions, PermissionsEnum.CanEditPaymentProviderOptions)
                  }
                  type='button'
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    type='button'
                    onClick={cancel}
                    variant='outline'
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSubmitForm} disabled={isSubmitting} type='button'>
                    Submit
                  </Button>
                </>
              )}
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard title='FX/CFD' columns={1}>
              <FormSelectField
                label='Check State'
                options={CheckStates}
                value={values.fxCFD?.checkState?.id}
                readOnly={!isEdit}
                onChange={(value) => {
                  const state = CheckStates.find((item) => item.id === Number(value))
                  if (state) {
                    setFieldValue('fxCFD.checkState', state)
                  }
                }}
              />
              <FormMultiSelectField
                label='Country'
                options={countries}
                value={values.fxCFD?.countryIds ?? []}
                readOnly={!isEdit}
                allowClear
                onChange={(value) => {
                  const nextValue = Array.isArray(value)
                    ? value.map(String)
                    : value == null
                      ? []
                      : [String(value)]
                  setFieldValue('fxCFD.countryIds', nextValue)
                }}
              />
              <Grid data={values.fxCFD?.pspOptions ?? []}>
                <Column field='paymentOption.name' title='Option Name' width={300} />
                <Column
                  field='status'
                  title='Status'
                  cell={(gridProps) => (
                    <StatusCell
                      {...gridProps}
                      disabled={!isEdit}
                      onChange={() => {
                        onChangeStatus(gridProps.dataItem.paymentOption.id, 'fxCFD')
                      }}
                    />
                  )}
                  width={80}
                />
                <Column
                  width={50}
                  title=''
                  filterCell={() => null}
                  cell={(gridCellProps: GridCellProps) => {
                    if (
                      gridCellProps.dataItem.paymentOption.id ===
                      WalletPaymentProviderOptionType.ValidationCheckForMaximumAmount
                    ) {
                      return (
                        <td>
                          <MoreButton
                            disabled={!isEdit}
                            items={[
                              {
                                name: 'Options',
                                onClick: () => openOptionModal(gridCellProps.dataItem, 'fxCFD'),
                              },
                            ]}
                          />
                        </td>
                      )
                    }

                    if (
                      gridCellProps.dataItem.paymentOption.id ===
                      WalletPaymentProviderOptionType.ValidationCheckForWithdrawalFrequency
                    ) {
                      return (
                        <td>
                          <MoreButton
                            disabled={!isEdit}
                            items={[
                              {
                                name: 'Options',
                                onClick: () => openFrequencyModal(gridCellProps.dataItem, 'fxCFD'),
                              },
                            ]}
                          />
                        </td>
                      )
                    }
                    return <td />
                  }}
                />
              </Grid>
            </DataCard>
          </DataColumn>
          <DataColumn>
            <DataCard title='IB Withdrawals' columns={1}>
              <FormSelectField
                label='Check State'
                options={CheckStates}
                value={values.ibFxCFD?.checkState?.id}
                readOnly={!isEdit}
                onChange={(value) => {
                  const state = CheckStates.find((item) => item.id === Number(value))
                  if (state) {
                    setFieldValue('ibFxCFD.checkState', state)
                  }
                }}
              />
              <FormMultiSelectField
                label='Country'
                options={countries}
                value={values.ibFxCFD?.countryIds ?? []}
                readOnly={!isEdit}
                allowClear
                onChange={(value) => {
                  const nextValue = Array.isArray(value)
                    ? value.map(String)
                    : value == null
                      ? []
                      : [String(value)]
                  setFieldValue('ibFxCFD.countryIds', nextValue)
                }}
              />
              <Grid data={values.ibFxCFD?.pspOptions ?? []}>
                <Column field='paymentOption.name' title='Option Name' width={300} />
                <Column
                  field='selected'
                  title='State'
                  cell={(gridProps) => (
                    <StatusCell
                      {...gridProps}
                      disabled={!isEdit}
                      onChange={() => {
                        if (isEdit) {
                          onChangeStatus(gridProps.dataItem.paymentOption.id, 'ibFxCFD')
                        }
                      }}
                    />
                  )}
                  width={80}
                />
                <Column
                  width={50}
                  title=''
                  filterCell={() => null}
                  cell={(gridCellProps: GridCellProps) => {
                    if (
                      gridCellProps.dataItem.paymentOption.id ===
                      WalletPaymentProviderOptionType.ValidationCheckForMaximumAmount
                    ) {
                      return (
                        <td>
                          <MoreButton
                            disabled={!isEdit}
                            items={[
                              {
                                name: 'Options',
                                onClick: () => openOptionModal(gridCellProps.dataItem, 'ibFxCFD'),
                              },
                            ]}
                          />
                        </td>
                      )
                    }

                    if (
                      gridCellProps.dataItem.paymentOption.id ===
                      WalletPaymentProviderOptionType.ValidationCheckForWithdrawalFrequency
                    ) {
                      return (
                        <td>
                          <MoreButton
                            disabled={!isEdit}
                            items={[
                              {
                                name: 'Options',
                                onClick: () =>
                                  openFrequencyModal(gridCellProps.dataItem, 'ibFxCFD'),
                              },
                            ]}
                          />
                        </td>
                      )
                    }
                    return <td />
                  }}
                />
              </Grid>
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </React.Fragment>
  )
}

interface StatusCellProps {
  disabled?: React.InputHTMLAttributes<HTMLInputElement>['disabled']
  onChange: React.ChangeEventHandler<HTMLInputElement>
}

const StatusCell: React.FC<PropsWithChildren<Omit<GridCellProps, 'onChange'>> & StatusCellProps> = (
  gridCellProps
) => {
  const { selected } = gridCellProps.dataItem
  return (
    <td align='center' style={{ textAlign: 'center', height: 50 }}>
      <input
        disabled={gridCellProps.disabled}
        type='checkbox'
        checked={selected}
        onChange={gridCellProps.onChange}
      />
    </td>
  )
}

interface OuterProps {
  paymentProvider: PspWdCheckPaymentOptionsDto
  countries: CountryDto[]

  onSubmit(values: PspWdCheckPaymentOptionsDto): Promise<void>
}

interface PaymentControlsPSPCheckStateValues {
  paymentProvider: NameDto<string>
  fxCFD: PspWdCheckWalletTypeOptions | null
  ibFxCFD: PspWdCheckWalletTypeOptions | null
}

export const PaymentControlsPSPCheckStateForm = withFormik<
  OuterProps,
  PaymentControlsPSPCheckStateValues
>({
  mapPropsToValues: (props) => {
    const ibFxCFD =
      props.paymentProvider.walletTypeOptions.find(
        (x) => x.walletType.id === WalletType.IB_FXCFD
      ) ?? null
    const fxCFD =
      props.paymentProvider.walletTypeOptions.find((x) => x.walletType.id === WalletType.FXCFD) ??
      null
    return {
      paymentProvider: props.paymentProvider.paymentProvider,
      fxCFD: {
        ...fxCFD,
        countryIds: fxCFD?.countryIds?.filter((x) => !!x) ?? [],
      } as PspWdCheckWalletTypeOptions,
      ibFxCFD: {
        ...ibFxCFD,
        countryIds: ibFxCFD?.countryIds?.filter((x) => !!x) ?? [],
      } as PspWdCheckWalletTypeOptions,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const walletTypeOptions: PspWdCheckWalletTypeOptions[] = [
        values.fxCFD as PspWdCheckWalletTypeOptions,
        values.ibFxCFD as PspWdCheckWalletTypeOptions,
      ]
      await props.onSubmit({ paymentProvider: values.paymentProvider, walletTypeOptions })
      setSubmitting(false)
    } catch (error) {
      console.error(error)
      setSubmitting(false)
    }
  },
  validate: () => {
    const errors: FormikErrors<WalletPaymentProviderOptionDto> = {}
    return errors
  },
  isInitialValid: false,
  enableReinitialize: true,
})(PaymentControlsPSPCheckStateFormUI)
